/**
 * Encapsulates CRUD and analysis actions for questions.
 * Delegates to questionsService and updates QuestionsContext.
 */

import { useCallback } from 'react';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useDiagramData } from '@/hooks/useDiagramData';
import { questionsService } from '@/services/questionsService';
import type { QuestionOut } from '@/services/questionsService';
import { vulnerabilityService } from '@/services/vulnerabilityService';
import { RateLimitError } from '@/lib/errors';
import { toast } from 'sonner';
import type { VulnerabilityFinding } from '@/types/ai';

export interface QuestionsActionsReturn {
  handleAdd: (question: string, domain: string, asrgIds: string) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  handleClearAll: () => Promise<void>;
  handleUpdateQuestion: (id: number, question: string) => Promise<void>;
  handleUpdateAnswer: (id: number, answer: string) => Promise<void>;
  handleUpdateNotes: (id: number, notes: string) => Promise<void>;
  handleUpdateFinding: (questionId: number, vulnId: number, updated: Partial<VulnerabilityFinding>) => Promise<void>;
  handleMarkStatus: (id: number, status: 'ok' | 'clarification' | 'vulnerable' | null) => Promise<void>;
  handleAnalyzeItem: (question: QuestionOut, setError: (msg: string | null) => void) => Promise<void>;
  handleLinkVulnerability: (questionId: number, vulnerabilityId: number) => Promise<void>;
  handleUnlinkVulnerability: (questionId: number, vulnerabilityId: number) => Promise<void>;
}

export function useQuestionsActions(currentProjectId: number): QuestionsActionsReturn {
  const {
    questions,
    setQuestions,
    setQuickScanResults,
    replaceQuestion,
    removeQuestion,
    setAnalyzingQuestionIds,
  } = useQuestions();
  const { currentDiagram } = useDiagramData();

  const handleAdd = useCallback(async (question: string, domain: string, asrgIds: string) => {
    if (!question.trim() || !domain.trim() || !currentProjectId) return;
    try {
      const created = await questionsService.createQuestion(currentProjectId, {
        security_domain: domain.trim(),
        asrg_ids: asrgIds.trim() || undefined,
        question: question.trim(),
      });
      setQuestions((prev) => [...prev, created]);
    } catch (err) {
      console.error('add question error:', err);
    }
  }, [currentProjectId, setQuestions]);

  const handleDelete = useCallback(async (id: number) => {
    if (!currentProjectId) return;
    try {
      await questionsService.deleteQuestion(currentProjectId, id);
      removeQuestion(id);
      setQuickScanResults((prev) => { const s = { ...prev }; delete s[id]; return s; });
    } catch (err) {
      console.error('delete question error:', err);
    }
  }, [currentProjectId, removeQuestion, setQuickScanResults]);

  const handleClearAll = useCallback(async () => {
    if (!currentProjectId) return;
    for (const q of questions) {
      try { await questionsService.deleteQuestion(currentProjectId, q.id); } catch { /* ignore */ }
    }
    setQuestions([]);
    setQuickScanResults({});
  }, [currentProjectId, questions, setQuestions, setQuickScanResults]);

  const handleUpdateQuestion = useCallback(async (id: number, question: string) => {
    if (!currentProjectId) return;
    try {
      const updated = await questionsService.updateQuestion(currentProjectId, id, { question });
      replaceQuestion(updated);
    } catch (err) {
      console.error('update question error:', err);
    }
  }, [currentProjectId, replaceQuestion]);

  const handleUpdateAnswer = useCallback(async (id: number, answer: string) => {
    if (!currentProjectId) return;
    try {
      const updated = await questionsService.updateQuestion(currentProjectId, id, { answer });
      replaceQuestion(updated);
    } catch (err) {
      console.error('update answer error:', err);
    }
  }, [currentProjectId, replaceQuestion]);

  const handleUpdateNotes = useCallback(async (id: number, notes: string) => {
    if (!currentProjectId) return;
    try {
      const updated = await questionsService.updateQuestion(currentProjectId, id, { notes: notes || null });
      replaceQuestion(updated);
    } catch (err) {
      console.error('update notes error:', err);
    }
  }, [currentProjectId, replaceQuestion]);

  const handleUpdateFinding = useCallback(async (questionId: number, vulnId: number, updated: Partial<VulnerabilityFinding>) => {
    const previousQuestions = questions;
    // Optimistic: update local state immediately
    setQuestions((prev) => prev.map((q): QuestionOut => {
      if (q.id !== questionId) return q;
      return {
        ...q,
        vulnerabilities: (q.vulnerabilities ?? []).map((v) =>
          v.id === vulnId
            ? {
                ...v,
                name: updated.vulnerability_title ?? v.name,
                vulnerability: updated.vulnerability_description ?? v.vulnerability,
                scenario: updated.threat_scenario ?? v.scenario,
                countermeasure: updated.countermeasure ?? v.countermeasure,
                rating_vector_string: updated.rating_vector_string !== undefined ? updated.rating_vector_string : v.rating_vector_string,
              }
            : v,
        ),
      };
    }));

    // Persist to DB (+ ThreatCat if applicable)
    try {
      const result = await vulnerabilityService.update(
        vulnId,
        {
          name: updated.vulnerability_title ?? undefined,
          vulnerability: updated.vulnerability_description ?? undefined,
          scenario: updated.threat_scenario ?? undefined,
          countermeasure: updated.countermeasure ?? undefined,
          rating_vector_string: updated.rating_vector_string,
        },
      );
      // Update with server response
      setQuestions((prev) => prev.map((q): QuestionOut => {
        if (q.id !== questionId) return q;
        return {
          ...q,
          vulnerabilities: (q.vulnerabilities ?? []).map((v) =>
            v.id === vulnId ? result : v,
          ),
        };
      }));
    } catch (err) {
      setQuestions(previousQuestions);
      toast.error(err instanceof Error ? err.message : 'Failed to update finding');
      console.error('update finding error:', err);
    }
  }, [questions, setQuestions]);

  const handleMarkStatus = useCallback(async (id: number, status: 'ok' | 'clarification' | 'vulnerable' | null) => {
    if (!currentProjectId) return;
    try {
      const updated = await questionsService.updateQuestion(currentProjectId, id, {
        manual_status: status,
      });
      replaceQuestion(updated);
    } catch (err) {
      console.error('mark status error:', err);
    }
  }, [currentProjectId, replaceQuestion]);

  const handleAnalyzeItem = useCallback(async (question: QuestionOut, setError: (msg: string | null) => void) => {
    if (!currentDiagram || !question.answer?.trim() || !currentProjectId) return;
    setAnalyzingQuestionIds((prev) => new Set(prev).add(question.id));
    try {
      // Persist user_context if it changed (comes from the analyze dialog)
      if (question.user_context !== undefined) {
        await questionsService.updateQuestion(currentProjectId, question.id, {
          user_context: question.user_context || null,
        });
      }
      const updated = await questionsService.analyze(currentProjectId, question.id, {
        entities: currentDiagram.entities,
        paths: currentDiagram.paths,
        boundaries: currentDiagram.boundaries,
      });
      replaceQuestion(updated);
      // Remove ephemeral quick-scan result since we now have a persisted vulnerability
      setQuickScanResults((prev) => { const s = { ...prev }; delete s[question.id]; return s; });
    } catch (err) {
      console.error('analyze-finding error:', err);
      setError(
        err instanceof RateLimitError
          ? `Rate limit reached. Please wait ${err.retryAfter ? `${err.retryAfter}s` : 'a moment'} before analyzing question ${question.id} again.`
          : err instanceof Error ? err.message : 'Vulnerability analysis failed'
      );
    } finally {
      setAnalyzingQuestionIds((prev) => { const s = new Set(prev); s.delete(question.id); return s; });
    }
  }, [currentDiagram, currentProjectId, replaceQuestion, setQuickScanResults, setAnalyzingQuestionIds]);

  const handleLinkVulnerability = useCallback(async (questionId: number, vulnerabilityId: number) => {
    if (!currentProjectId) return;
    try {
      const updated = await questionsService.linkVulnerability(currentProjectId, questionId, vulnerabilityId);
      replaceQuestion(updated);
    } catch (err) {
      console.error('link vulnerability error:', err);
    }
  }, [currentProjectId, replaceQuestion]);

  const handleUnlinkVulnerability = useCallback(async (questionId: number, vulnerabilityId: number) => {
    if (!currentProjectId) return;
    try {
      const updated = await questionsService.unlinkVulnerability(currentProjectId, questionId, vulnerabilityId);
      replaceQuestion(updated);
    } catch (err) {
      console.error('unlink vulnerability error:', err);
    }
  }, [currentProjectId, replaceQuestion]);

  return {
    handleAdd,
    handleDelete,
    handleClearAll,
    handleUpdateQuestion,
    handleUpdateAnswer,
    handleUpdateNotes,
    handleUpdateFinding,
    handleMarkStatus,
    handleAnalyzeItem,
    handleLinkVulnerability,
    handleUnlinkVulnerability,
  };
}
